import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { FormattedMessage } from '../../../../util/reactIntl';
import { goToLogin, hereOf } from '../../../../util/authReturn';
import { fetchMyGarage } from '../../../../ducks/myGarage.duck';
import { fetchFinanceStatus } from '../../../../ducks/financeStatus.duck';
import {
  getTradeIn,
  getTradeInLabel,
  getTradeInNegativeEquity,
} from '../../../../ducks/tradeIn.duck';
import {
  garageSummary,
  tradeInSummary,
  financeSummary,
} from '../../../../util/myTransactionSummaries';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  TradeInForm,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import { useGeoCountry, getCountryConfig } from '../../../../hooks/useGeoCountry';

const LanguageToggle = () => {
  const [lang, setLang] = React.useState(
    typeof window !== 'undefined' && localStorage.getItem('skip_lang') === 'fr' ? 'fr' : 'en'
  );
  const toggle = () => {
    const next = lang === 'en' ? 'fr' : 'en';
    setLang(next);
    if (typeof window !== 'undefined') localStorage.setItem('skip_lang', next);
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        border: '1.5px solid #e2e8f0',
        borderRadius: 8,
        overflow: 'hidden',
        marginLeft: 4,
      }}
    >
      {['EN', 'FR'].map(l => (
        <button
          key={l}
          onClick={toggle}
          style={{
            padding: '5px 11px',
            fontSize: 12,
            fontWeight: 700,
            background: lang === l.toLowerCase() ? '#1A9E85' : '#fff',
            color: lang === l.toLowerCase() ? '#fff' : '#4a5e6e',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'DM Sans, sans-serif',
            letterSpacing: '0.05em',
          }}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const SignInRegisterButton = () => {
  return (
    <NamedLink
      name="LoginPage"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        background: '#1A9E85',
        color: '#fff',
        fontWeight: 700,
        fontSize: 13.5,
        padding: '8px 20px',
        borderRadius: 50,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        fontFamily: 'DM Sans, sans-serif',
        letterSpacing: '0.01em',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <ellipse cx="12" cy="12" rx="4" ry="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
      </svg>
      Sign in / Register
    </NamedLink>
  );
};

// Exported for testing — the garage card's fetch-on-open behaviour and its
// summary states are worth pinning independently of the whole topbar.
export const MyTransactionPanel = ({ currentUser }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const garage = useSelector(state => state.myGarage);
  const [tradeInFormOpen, setTradeInFormOpen] = useState(false);
  const finance = useSelector(state => state.financeStatus);
  const isSignedIn = !!currentUser?.id;

  // The trade-in record already rides along on currentUser (privateData is
  // returned with currentUser.show), so there is nothing to fetch for it.
  const tradeIn = getTradeIn(currentUser);

  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetched when the panel opens, not on mount: the topbar is on every route,
  // and a signed-out visitor has no garage to fetch.
  //
  // At most one attempt per open. Without this guard a failed fetch spins:
  // the rejection leaves `loaded` false and `fetchInProgress` false, which is
  // exactly the condition that triggers another fetch, so an outage would
  // turn an open panel into a retry loop against the API. Closing the panel
  // arms the next attempt, so reopening still retries.
  const attemptedThisOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      attemptedThisOpenRef.current = false;
      return;
    }
    if (!isSignedIn || garage.loaded || garage.fetchInProgress || attemptedThisOpenRef.current) {
      return;
    }
    attemptedThisOpenRef.current = true;
    dispatch(fetchMyGarage());
  }, [open, isSignedIn, garage.loaded, garage.fetchInProgress, dispatch]);

  // Same one-attempt-per-open guard as the garage, for the same reason: a
  // failed lookup leaves loaded=false, which is the condition that triggers it.
  const financeAttemptedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      financeAttemptedRef.current = false;
      return;
    }
    if (!isSignedIn || finance.loaded || finance.fetchInProgress || financeAttemptedRef.current) {
      return;
    }
    financeAttemptedRef.current = true;
    dispatch(fetchFinanceStatus());
  }, [open, isSignedIn, finance.loaded, finance.fetchInProgress, dispatch]);

  const actions = [
    {
      icon: '🚗',
      label: 'My garage',
      summary: garageSummary(garage, isSignedIn),
      // Signed-out sellers are sent to sign-in rather than a page that would
      // bounce them there anyway.
      onClick: () => {
        setOpen(false);
        if (isSignedIn) history.push('/listings');
        else goToLogin(history, '/listings'); // come back to the garage after signing in
      },
    },
    {
      icon: '🔄',
      label: 'My trade-in',
      summary: tradeInSummary(tradeIn, isSignedIn),
      onClick: () => {
        setOpen(false);
        if (isSignedIn) setTradeInFormOpen(true);
        else goToLogin(history, hereOf(history)); // return to this page (the vehicle being viewed)
      },
    },
    {
      icon: '💳',
      label: 'Finance',
      summary: financeSummary(finance, isSignedIn),
      onClick: () => {
        setOpen(false);
        if (isSignedIn) history.push('/listings');
        else goToLogin(history, '/listings');
      },
    },
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: open ? '#f0fbf8' : '#fff',
          border: '1.5px solid #1A9E85',
          color: '#1A9E85',
          fontWeight: 700,
          fontSize: 13,
          padding: '7px 16px',
          borderRadius: 50,
          cursor: 'pointer',
          fontFamily: 'DM Sans, sans-serif',
        }}
      >
        My Transaction
        <span style={{ fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            right: 0,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 16,
            boxShadow: '0 12px 40px rgba(15,32,51,0.18)',
            minWidth: 360,
            zIndex: 999,
            overflow: 'hidden',
          }}
        >
          {/* Step progress */}
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid #f0f4f8',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: '#9aaab8', marginRight: 4 }}>
              MY TRANSACTION
            </span>
            {['🚗', '🔄', '💳'].map((icon, i) => (
              <React.Fragment key={i}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: i === 0 ? '#e8f7f4' : '#f4f7fa',
                    border: `1.5px solid ${i === 0 ? '#1A9E85' : '#e2e8f0'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 15,
                  }}
                >
                  {icon}
                </div>
                {i < 2 && <div style={{ width: 16, height: 1.5, background: '#e2e8f0' }} />}
              </React.Fragment>
            ))}
            <button
              onClick={() => setOpen(false)}
              style={{
                marginLeft: 'auto',
                background: '#f4f7fa',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 600,
                color: '#4a5e6e',
                cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              Close ▲
            </button>
          </div>

          {/* Action cards */}
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9aaab8', marginBottom: 12 }}>
              MY ACTIONS
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {actions.map((a, i) => {
                const isInteractive = !!a.onClick;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={a.onClick}
                    // Cards without a destination yet stay inert rather than
                    // looking clickable and doing nothing.
                    disabled={!isInteractive}
                    style={{
                      background: '#f4f7fa',
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: '16px 8px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      cursor: isInteractive ? 'pointer' : 'default',
                      fontFamily: 'inherit',
                      textAlign: 'center',
                      width: '100%',
                    }}
                    onMouseEnter={e => {
                      if (!isInteractive) return;
                      e.currentTarget.style.borderColor = '#1A9E85';
                      e.currentTarget.style.background = '#f0fbf8';
                    }}
                    onMouseLeave={e => {
                      if (!isInteractive) return;
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.background = '#f4f7fa';
                    }}
                  >
                    <div style={{ fontSize: 24 }}>{a.icon}</div>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: '#4a5e6e' }}>
                      {a.label}
                    </span>
                    {a.summary ? (
                      <span
                        style={{
                          fontSize: 10.5,
                          lineHeight: 1.3,
                          color: '#7a8c99',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {a.summary}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Rendered outside the `open &&` block: opening the form closes the
          panel, and unmounting the modal with it would close the form too. */}
      <TradeInForm isOpen={tradeInFormOpen} onClose={() => setTradeInFormOpen(false)} />
    </div>
  );
};

/**
 * Buy, Sell and the Garage, in the header itself rather than behind a menu.
 *
 * Buy shows to everyone. Sell follows the same visibility rule as the other
 * "post a listing" links (showCreateListingLinkForUser), so the header, the
 * mobile menu and the custom links menu never disagree about who may post.
 */
const PrimaryNavLinks = ({ currentPage, showCreateListingsLink }) => {
  const currentClass = page => (currentPage === page ? css.currentPage : null);
  return (
    <>
      <NamedLink
        id="buy-link"
        className={classNames(css.topbarLink, currentClass('SearchPage'))}
        name="SearchPage"
      >
        <span className={css.topbarLinkLabel}>
          <FormattedMessage id="TopbarDesktop.buy" />
        </span>
      </NamedLink>
      {showCreateListingsLink ? (
        <NamedLink
          id="sell-link"
          className={classNames(css.topbarLink, currentClass('NewListingPage'))}
          name="NewListingPage"
        >
          <span className={css.topbarLinkLabel}>
            <FormattedMessage id="TopbarDesktop.sell" />
          </span>
        </NamedLink>
      ) : null}
    </>
  );
};

/**
 * The Garage. It was reachable only from inside the avatar menu, which is where
 * account settings live — not where a seller looks for their own vehicles.
 */
const GarageLink = ({ currentPage }) => (
  <NamedLink
    id="garage-link"
    className={classNames(
      css.topbarLink,
      currentPage === 'ManageListingsPage' ? css.currentPage : null
    )}
    name="ManageListingsPage"
  >
    <span className={css.topbarLinkLabel}>
      <FormattedMessage id="TopbarDesktop.garage" />
    </span>
  </NamedLink>
);

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      id="inbox-link"
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: inboxTab }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, intl }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const garageLinkMaybe = authenticatedOnClientSide ? (
    <GarageLink currentPage={currentPage} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = null;
  const loginLinkMaybe = null;
  const signInButtonMaybe = isAuthenticatedOrJustHydrated ? null : <SignInRegisterButton />;
  const myTransactionMaybe = authenticatedOnClientSide ? (
    <MyTransactionPanel currentUser={currentUser} />
  ) : null;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav
      className={classes}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
    >
      <LinkedLogo
        id="logo-topbar-desktop"
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      {searchFormMaybe}

      <PrimaryNavLinks currentPage={currentPage} showCreateListingsLink={showCreateListingsLink} />
      {garageLinkMaybe}

      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        showCreateListingsLink={showCreateListingsLink}
      />

      {inboxLinkMaybe}
      {myTransactionMaybe}
      {profileMenuMaybe}
      {signInButtonMaybe}
      {signupLinkMaybe}
      {loginLinkMaybe}
      <LanguageToggle />
    </nav>
  );
};

export default TopbarDesktop;
